import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * ModalDumb
 *
 * ModalDumb component creates a React Portal (see https://reactjs.org/docs/portals.html)
 * Portals provide a first-class way to render children into a DOM node that exists outside the DOM hierarchy of the parent component.
 *
 * Portal implementations examples
 * https://reactjs.org/docs/portals.html#event-bubbling-through-portals
 * https://blog.logrocket.com/learn-react-portals-by-example/
 *
 * Above shows examples mirroring this direction of implementing Portals
 *
 * @param props
 * @returns
 */
export const ModalDumb = ({
    children,
}: {
    children: ReactNode;
}): JSX.Element => {
    // Get the portal container we have injected in `gatsby-ssr.js`
    const mount = document.getElementById('portal');

    // Create an element within our portal div to attach the Modal to
    const el = document.createElement('div');

    // Add the element when the component renders and clean up when unmounted
    useEffect(() => {
        if (mount) {
            mount.appendChild(el);
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
        }
        return () => {
            if (mount) {
                mount.removeChild(el);
                document.documentElement.style.overflow = '';
                document.body.style.overflow = '';
            }
        };
    }, [el, mount]);

    // Return the React Portal
    return createPortal(children, el);
};
